import { FC, useEffect, useRef, useState } from 'react'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Menu from './Menu'
import Box from '@mui/material/Box'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import MuiLink from '@mui/material/Link'
import Button from '@mui/material/Button'
import Icon from '@brand/Icon'
import { debounce } from '@util/func'
import useGlobalContext from '@context/GlobalContext/useGlobalContext'
import UserMenu from './UserMenu'
import { Grid, useMediaQuery, useTheme } from '@mui/material'
import useHeaderDetails from './useHeaderDetails'
import useAnalytics from '@hooks/useAnalytics'
import Breadcrumbs from '../Breadcrumbs/index'
import localStore from '@service/local-store'
import ProviderMessageNotifications from './ProviderMessageNotifications'
import useAdobeAnalytics, {
  PATIENT_EVENTS,
  SHARED_EVENTS,
} from '@analytics/adobe'
import { SURVEY_STATES } from '@util/surveyState'
import { PATIENTS } from '../../route.const'
import TermsOfServiceModal from '../Home/TermsOfServiceModal'
import { useFlags } from 'launchdarkly-react-client-sdk'

const Header: FC = () => {
  const [openTOS, setOpenTOS] = useState(false)
  const { enableHomeDesignV2: enableHomeDesignV2Flag } = useFlags() as {
    enableHomeDesignV2?: boolean
  }
  const enableHomeDesignV2: boolean = enableHomeDesignV2Flag ?? false
  const { login, role, user, topMenu, bottomMenu, surveyState } =
    useHeaderDetails()
  const legacyTopMenu = enableHomeDesignV2
    ? topMenu
    : topMenu.filter((item) => item.label !== 'FAQ')
  const location = useLocation()
  const navigate = useNavigate()
  const { setHeaderHeightInPx } = useGlobalContext()
  const ref = useRef<HTMLElement>()
  const { breakpoints } = useTheme()
  const { trackLink } = useAdobeAnalytics()
  const isMobile = useMediaQuery(breakpoints.down('lg'))
  const { trackLogout } = useAnalytics()
  const resizeHandler = debounce(() => {
    setHeaderHeightInPx(ref.current?.clientHeight || 64)
  })
  const userType = localStore.get('userType')
  const staticMenuSuppressedPaths = [
    '/settings',
    '/contact-us',
    '/about-us',
    '/privacy-policy',
    '/terms-of-use',
    '/faq',
  ]
  const hideHeaderMenus =
    login && staticMenuSuppressedPaths.includes(location.pathname.toLowerCase())
  const isCampaignPage = location.pathname.startsWith('/lp/')

  const onUnload = () => {
    if (login) {
      trackLogout(role, 'tab-exit')
    }
  }
  useEffect(() => {
    window.addEventListener('beforeunload', onUnload)
    return () => {
      window.removeEventListener('beforeunload', onUnload)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login, trackLogout])
  useEffect(() => {
    resizeHandler()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  useEffect(() => {
    window.addEventListener('resize', resizeHandler)
    return () => {
      window.removeEventListener('resize', resizeHandler)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const validationToNavigation = () => {
    // Determine analytics location based on authentication status
    let analyticsLocation = 'unauthenticated-headers'
    if (login) {
      analyticsLocation =
        role === PATIENTS
          ? 'authenticated patient - headers'
          : 'authenticated provider - headers'
    }

    trackLink(
      {
        name: login ? PATIENT_EVENTS.CLICK.LOGO : SHARED_EVENTS.NAV_CLICK.LOGO,
        location: analyticsLocation,
        type: 'internal',
        destinationUrl: login
          ? surveyState === SURVEY_STATES.COMPLETED
            ? `/${role}`
            : undefined
          : '/',
      },
      {
        loginStatus: login ? 'logged in' : 'logged out',
        userType: role,
      }
    )
    login
      ? surveyState === SURVEY_STATES.COMPLETED && navigate(`/${role}`)
      : navigate('/')
  }

  return (
    // @ts-ignore
    <AppBar position={'relative'} color={'transparent'} ref={ref}>
      <Toolbar
        sx={({ ues }) => ({
          p: { xs: 0, sm: 0, md: 0 },
          py: { xs: 0, sm: 0, md: 0, lg: '11px', xl: '11px' },
          background: enableHomeDesignV2
            ? ues.neutral[0]
            : isCampaignPage
              ? ues.neutral[0]
              : ues.primary['5'],
          minHeight: {
            xs: '48px',
            sm: '48px',
            md: '48px',
            lg: '85px',
            xl: '85px',
          },
        })}
      >
        {isMobile ? (
          <>
            <Grid
              container
              spacing={1}
              alignItems={'center'}
              justifyContent={'space-between'}
              width={'100%'}
            >
              <Grid item xs={4}>
                <Box sx={{ pl: 2, pt: 1.5, pb: 1.5, textAlign: 'left' }}>
                  <Icon
                    id={'brandIcon'}
                    name={'brand'}
                    height={'26px'}
                    width={'90px'}
                    onClick={() => validationToNavigation()}
                    cursor={'pointer'}
                    sx={{ outlineWidth: '0px !important' }}
                  />
                </Box>
              </Grid>
              <Grid
                item
                xs={4}
                sx={{ display: 'flex', justifyContent: 'flex-end' }}
              >
                {!isCampaignPage && (
                  <>
                    {login ? (
                      <>
                        <Box display={'flex'} alignItems={'center'}>
                          {login && role.toLowerCase() === 'providers' && (
                            <Box sx={{ mr: '-30px', mt: '3px' }}>
                              <ProviderMessageNotifications />
                            </Box>
                          )}
                          {!hideHeaderMenus && bottomMenu.length > 0 && (
                            <Box sx={{ float: 'right' }}>
                              <Menu
                                pages={bottomMenu}
                                username={user?.displayName || ''}
                                isLogin={login}
                                degrees={user?.degrees || ''}
                                avatar={user?.avatar || ''}
                              />
                            </Box>
                          )}
                          <Box sx={{ float: 'right' }}>
                            {!hideHeaderMenus &&
                            (location.pathname.includes('expectation') ||
                              location.pathname === 'privacy') ? (
                              <Menu
                                pages={legacyTopMenu}
                                username={user?.displayName || ''}
                                isLogin={login}
                                degrees={user?.degrees || ''}
                                avatar={user?.avatar || ''}
                              />
                            ) : (
                              <UserMenu
                                key={user?.displayName || '0'}
                                username={user?.displayName || ''}
                                isLogin={login}
                                degrees={user?.degrees || ''}
                                avatar={user?.avatar || ''}
                              />
                            )}
                          </Box>
                        </Box>
                      </>
                    ) : (
                      !(
                        userType === 'guestUser' &&
                        location.pathname.includes('/patients/survey')
                      ) && (
                        <>
                          {!hideHeaderMenus && bottomMenu.length > 0 ? (
                            <Box sx={{ float: 'right' }}>
                              <Menu
                                pages={bottomMenu}
                                username={user?.displayName || ''}
                                isLogin={login}
                                degrees={user?.degrees || ''}
                                avatar={user?.avatar || ''}
                              />
                            </Box>
                          ) : (
                            <Box sx={{ float: 'right' }}>
                              {!hideHeaderMenus && (
                                <Menu
                                  pages={legacyTopMenu}
                                  username={user?.displayName || ''}
                                  isLogin={login}
                                  degrees={user?.degrees || ''}
                                  avatar={user?.avatar || ''}
                                />
                              )}
                            </Box>
                          )}
                        </>
                      )
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <Box
              width={'100%'}
              sx={{
                pt: { md: '24px', lg: '24px', xl: '24px' },
                px: {
                  md: isCampaignPage ? '32px' : '24px',
                  lg: '84px',
                  xl: '84px',
                },
              }}
            >
              <Box width={'100%'}>
                <Box
                  width={'100%'}
                  display={'flex'}
                  justifyContent={'space-between'}
                  sx={{
                    ...(enableHomeDesignV2 && { position: 'relative' }),
                    paddingY: {
                      xs: 0,
                      sm: 0,
                      md: '10px',
                      lg: '10px',
                      xl: '10px',
                    },
                    marginBottom: {
                      xs: 0,
                      sm: 0,
                      md: '12px',
                      lg: '12px',
                      xl: '12px',
                    },
                    alignItems: 'center',
                  }}
                >
                  <Icon
                    id={'brandIcon'}
                    name={'brand'}
                    height={'51px'}
                    width={'176px'}
                    onClick={() => {
                      // Single canonical event only (avoid overriding om_nav_click:logo)
                      validationToNavigation()
                      // Additional tracking only for authenticated providers (not patients)
                      if (login && role !== 'patients') {
                        trackLink(
                          {
                            name: `Optum Logo(Nav)`,
                            location: 'Main content',
                            type: 'internal',
                            description:
                              'User clicks logo to return to landing page',
                          },
                          {
                            loginStatus: 'logged in',
                            userType: role,
                          }
                        )
                      }
                    }}
                    cursor={'pointer'}
                    role="img"
                    aria-label="Optum Logo"
                    tabIndex={0}
                    sx={{ outlineWidth: '0px !important' }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        validationToNavigation()
                      }
                    }}
                  />
                  {!isCampaignPage && !login && enableHomeDesignV2 && (
                    <Box
                      component="nav"
                      sx={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: 4,
                        alignItems: 'center',
                      }}
                    >
                      {topMenu.map((item) => (
                        <MuiLink
                          key={item.label}
                          component={Link}
                          to={item.path || ''}
                          sx={({ ues }) => ({
                            color: ues.neutral['80'],
                            fontWeight: 700,
                            fontSize: '16px',
                            textDecoration: 'none',
                            '&:hover': { color: ues.primary[100] },
                          })}
                        >
                          {item.label}
                        </MuiLink>
                      ))}
                    </Box>
                  )}
                  {!isCampaignPage && (
                    <Box>
                      {login ? (
                        <>
                          <Box display={'flex'}>
                            {login && role.toLowerCase() === 'providers' && (
                              <Box>
                                <ProviderMessageNotifications />
                              </Box>
                            )}
                            <Box>
                              <UserMenu
                                username={user?.displayName || ''}
                                isLogin={login}
                                degrees={user?.degrees || ''}
                                avatar={user?.avatar || ''}
                              />
                            </Box>
                          </Box>
                        </>
                      ) : (
                        !(
                          userType === 'guestUser' &&
                          location.pathname.includes('/patients/survey')
                        ) &&
                        (enableHomeDesignV2 ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              height: '100%',
                            }}
                          >
                            {!location.pathname.includes(
                              '/providers/not-authorized'
                            ) && (
                              <UserMenu
                                username={'Sign in'}
                                isLogin={login}
                                degrees={user?.degrees || ''}
                                avatar={user?.avatar || ''}
                              />
                            )}
                            <Button
                              variant={'contained'}
                              onClick={() => setOpenTOS(true)}
                              sx={({ ues }) => ({
                                color: ues.neutral['0'],
                                backgroundColor: ues.primary[130],
                                fontSize: '14px',
                                fontWeight: 700,
                                borderRadius: '50px',
                                px: 2.5,
                                py: '6px',
                                whiteSpace: 'nowrap',
                                textTransform: 'none',
                                lineHeight: '24px',
                                alignSelf: 'center',
                                mt: 0,
                                '&:hover': {
                                  backgroundColor: ues.primary[130],
                                  boxShadow: 'none',
                                },
                              })}
                            >
                              Find your match
                            </Button>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex' }}>
                            {!hideHeaderMenus && (
                              <Menu
                                pages={legacyTopMenu}
                                username={user?.displayName || ''}
                                isLogin={login}
                                degrees={user?.degrees || ''}
                                avatar={user?.avatar || ''}
                              />
                            )}
                            {!location.pathname.includes(
                              '/providers/not-authorized'
                            ) && (
                              <UserMenu
                                username={'Sign in'}
                                isLogin={login}
                                degrees={user?.degrees || ''}
                                avatar={user?.avatar || ''}
                              />
                            )}
                          </Box>
                        ))
                      )}
                    </Box>
                  )}
                </Box>
                {!isCampaignPage &&
                  !hideHeaderMenus &&
                  bottomMenu.length > 0 && (
                    <Box pt={{ xs: 0, sm: 1, md: 2, lg: 3, xl: 3 }}>
                      <Menu
                        pages={bottomMenu}
                        username={user?.displayName || ''}
                        isLogin={login}
                        degrees={user?.degrees || ''}
                        avatar={user?.avatar || ''}
                      />
                    </Box>
                  )}
              </Box>
            </Box>
          </>
        )}
      </Toolbar>
      {!isCampaignPage && <Breadcrumbs isLoggedIn={login} />}
      <TermsOfServiceModal open={openTOS} setOpen={setOpenTOS} />
    </AppBar>
  )
}

export default Header
